import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

// What the page says when the New song email did not go out (the song itself is always saved).
export const NOT_SENT = {
  address: "The song was added, but the email was NOT sent: save your mailing address under Email to members first.",
  already: "This song was already emailed.",
  nobody: "The song was added. Nobody is on the email list yet, so no email went out.",
  error: "The song was added, but something went wrong, so no email went out. Try the Email members button on the song.",
  sending: "This email is still going out. Check back in a minute.",
};

// Same for the welcome email.
const WELCOME_NOT_SENT = {
  address: "Not sent: save your mailing address above first.",
  nobody: "Everyone has had the welcome email already.",
  sending: "The welcome emails are still going out. Check back in a minute.",
  error: "Something went wrong, so no email went out. Try again.",
};

// Same for the Featured song email (there is no song being added here).
const FEATURED_NOT_SENT = {
  address: "Not sent: save your mailing address above first.",
  nobody: "Not sent: nobody is on the email list yet.",
  sending: "An email about this song is going out, or just went out. Check back in a minute.",
  error: "Something went wrong, so no email went out. Try again.",
};

// Same for the monthly roundup and resubscribe emails.
const MORE_NOT_SENT = {
  address: "Not sent: save your mailing address above first.",
  already: "This month's email was already sent.",
  sending: "This email is still going out. Check back in a minute.",
  nobody: "Nobody to send it to this month.",
  nosongs: "No new songs to send yet.",
  error: "Something went wrong, so no email went out. Try again.",
};

const dayText = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; }
};

// The three come-back and monthly emails, in the order they are shown.
const MORE_ROWS = [
  {
    kind: "comeback", title: "Come-back email", test: "Email me a test of the come-back",
    text: "For members who have not visited in over 30 days (up to a year) and never paid for Premium. They get it once each time they drift away: the new songs since their last visit, with covers they can tap.",
  },
  {
    kind: "resubscribe", title: "Resubscribe email", test: "Email me a test of the resubscribe",
    text: "For members who paid for Premium before and have not visited in over 30 days (up to a year). Once a month: the new songs since their last visit, and a button back to Premium.",
  },
  {
    kind: "roundup", title: "Monthly roundup", test: "Email me a test of the roundup",
    text: "For Premium members and members who visited in the last 30 days. Once a month: the songs added since the last roundup, with covers they can tap. Nothing is sent in a month with no new songs.",
  },
];

function Switch({ on, label, labelId, onClick, disabled, title, testid }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-labelledby={labelId} onClick={onClick} disabled={disabled} data-testid={testid} title={title} aria-label={labelId ? undefined : label}
      className={`shrink-0 relative w-12 h-7 rounded-full border transition-colors disabled:opacity-60 ${on ? "bg-cyan-400 border-cyan-300" : "bg-white/10 border-white/20"}`}>
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

const lastText = (l) => {
  if (!l) return "";
  if (l.status === "sending") return `Sending... ${l.sent + l.failed} of ${l.total}`;
  if (l.status === "skipped") return `${dayText(l.created_at)}: not sent (${l.reason === "nosongs" ? "no new songs" : "nobody to send to"})`;
  return `${dayText(l.created_at)}: ${l.sent} sent${l.failed ? `, ${l.failed} could not be sent` : ""}`;
};

function MailStatus({ mail }) {
  if (!mail) return <span className="text-zinc-500">Not emailed</span>;
  if (mail.status === "sending") return <span className="text-cyan-300">Sending... {mail.sent + mail.failed} of {mail.total}</span>;
  const left = mail.missed > 0 ? `, ${mail.missed} did not get it` : mail.failed ? `, ${mail.failed} could not be sent` : "";
  return (
    <span className="text-zinc-300">
      Emailed {dayText(mail.created_at)}: {mail.sent} sent{left}
    </span>
  );
}

// Admin: the New song email. Mailing address, a test to yourself, and the latest songs with what was emailed.
export default function AdminMailers() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState("");
  const filled = useRef(false);
  const [pick, setPick] = useState("");        // song chosen for the Featured song email
  const [note, setNote] = useState("");        // Dan's optional extra line
  const [welcome, setWelcome] = useState(null);  // the welcome email box: switch, how many are waiting, the last once-only run
  const [more, setMore] = useState(null);        // the come-back, resubscribe and monthly roundup box
  const [leave, setLeave] = useState(null);      // the leave-out list as saved: { text, count, on_site }
  const [leaveText, setLeaveText] = useState(""); // what is typed in the leave-out box
  const leaveFilled = useRef(false);
  const [signups, setSignups] = useState(null);   // people who signed up in the footer without a login: counts and the latest few
  const [dj, setDj] = useState(null);             // the DJ Booth introduction box: switch and counts
  const [djWeek, setDjWeek] = useState("1");      // which week of the DJ Booth email "Email me a test" sends

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/mail/overview");
      setData(r.data); setFailed(false);
      if (!filled.current) { setAddress(r.data.address || ""); filled.current = true; }   // never overwrite what is being typed
    } catch { setFailed(true); }
  }, []);

  const loadWelcome = useCallback(async () => {
    try { const r = await api.get("/admin/mail/welcome"); setWelcome(r.data); } catch { /* the rest of the panel still works */ }
  }, []);

  const loadMore = useCallback(async () => {
    try { const r = await api.get("/admin/mail/more"); setMore(r.data); } catch { /* the rest of the panel still works */ }
  }, []);

  const loadDj = useCallback(async () => {
    try { const r = await api.get("/admin/mail/dj"); setDj(r.data); } catch { /* the rest of the panel still works */ }
  }, []);

  const loadLeave = useCallback(async () => {
    try {
      const r = await api.get("/admin/mail/exclude");
      setLeave(r.data);
      if (!leaveFilled.current) { setLeaveText(r.data.text || ""); leaveFilled.current = true; }   // never overwrite what is being typed
    } catch { /* the rest of the panel still works */ }
  }, []);

  const loadSignups = useCallback(async () => {
    try { const r = await api.get("/admin/mail/subscribers"); setSignups(r.data); } catch { /* the rest of the panel still works */ }
  }, []);

  useEffect(() => {
    load();
    loadWelcome();
    loadMore();
    loadLeave();
    loadSignups();
    loadDj();
    const again = () => { load(); loadWelcome(); loadMore(); loadSignups(); loadDj(); };
    window.addEventListener("dtm-mail-refresh", again);
    return () => window.removeEventListener("dtm-mail-refresh", again);
  }, [load, loadWelcome, loadMore, loadLeave, loadSignups, loadDj]);

  const sending = !!data && (data.songs.some((s) => s.mail && s.mail.status === "sending") || (data.featured || []).some((f) => f.status === "sending"));
  const welcomeSending = !!welcome && !!welcome.run && welcome.run.status === "sending";
  const moreSending = !!more && ["resubscribe", "roundup"].some((k) => more[k].last && more[k].last.status === "sending");
  useEffect(() => {
    if (!sending && !welcomeSending && !moreSending) return undefined;
    const t = setInterval(() => { if (sending) load(); if (welcomeSending) loadWelcome(); if (moreSending) loadMore(); }, 6000);
    return () => clearInterval(t);
  }, [sending, welcomeSending, moreSending, load, loadWelcome, loadMore]);

  const saveAddress = async () => {
    setBusy("address");
    try {
      const r = await api.put("/admin/mail/address", { address });
      setAddress(r.data.address);
      toast.success(r.data.address ? "Mailing address saved" : "Mailing address cleared. No emails will go out until you add one.");
      window.dispatchEvent(new Event("dtm-mail-address"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the address"); }
    finally { setBusy(""); }
  };

  const saveLeave = async () => {
    setBusy("leave");
    try {
      const r = await api.put("/admin/mail/exclude", { text: leaveText });
      setLeave(r.data); setLeaveText(r.data.text || "");
      toast.success(r.data.count ? `Saved. ${r.data.count} address${r.data.count === 1 ? "" : "es"} will not get these emails.` : "Saved. The list is empty.");
      await Promise.all([load(), loadWelcome(), loadMore(), loadSignups(), loadDj()]);     // the counts change
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the list"); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const r = await api.post("/admin/mail/test");
      toast.success(`Test sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the test"); }
    finally { setBusy(""); }
  };

  const sendFor = async (s) => {
    if (!window.confirm(`Email ${data.count} people about "${s.title}"? This cannot be undone.`)) return;
    setBusy(s.id);
    try {
      const r = await api.post(`/admin/mail/send/${s.id}`);
      if (r.data.queued) toast.success(`Emailing ${r.data.queued} people now.`);
      else toast.error(NOT_SENT[r.data.reason] || "The email was not sent.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the email"); }
    finally { setBusy(""); }
  };

  const flipWelcomeAuto = async () => {
    setBusy("welcome-auto");
    try {
      const r = await api.put("/admin/mail/welcome/auto", { on: !welcome.auto });
      toast.success(r.data.auto ? "New members will get the welcome email from now on" : "The automatic welcome email is off");
      await loadWelcome();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change that"); }
    finally { setBusy(""); }
  };

  const testWelcome = async () => {
    setBusy("welcome-test");
    try {
      const r = await api.post("/admin/mail/welcome/test");
      toast.success(`Test sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the test"); }
    finally { setBusy(""); }
  };

  const sendWelcomeExisting = async () => {
    if (!window.confirm(`Send the welcome email once to the ${welcome.waiting} members who have not had it? Nobody gets it twice. This cannot be undone.`)) return;
    setBusy("welcome-existing");
    try {
      const r = await api.post("/admin/mail/welcome/send-existing");
      if (r.data.queued) toast.success(`Emailing ${r.data.queued} members now.`);
      else toast.error(WELCOME_NOT_SENT[r.data.reason] || "The email was not sent.");
      await loadWelcome();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the email"); }
    finally { setBusy(""); }
  };

  // Come-back, resubscribe and monthly roundup: a switch each, a test to yourself, and (monthly ones) a send-now button.
  const flipMore = async (row) => {
    const cur = more[row.kind];
    if (!cur.auto && row.kind === "comeback" && cur.due > 0
        && !window.confirm(`Turn on the come-back email?\n\n${cur.due} members who have been away will get it over the next few minutes. After that, members who drift away get it automatically.`)) return;
    setBusy(`more-${row.kind}`);
    try {
      await api.put("/admin/mail/more/auto", { kind: row.kind, on: !cur.auto });
      toast.success(!cur.auto ? `${row.title} is on` : `${row.title} is off`);
      await loadMore();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change that"); }
    finally { setBusy(""); }
  };

  const testMore = async (row) => {
    setBusy(`more-test-${row.kind}`);
    try {
      const r = await api.post("/admin/mail/more/test", { kind: row.kind });
      toast.success(`Test sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the test"); }
    finally { setBusy(""); }
  };

  const sendMore = async (row) => {
    const cur = more[row.kind];
    if (!window.confirm(`Send this month's ${row.title.toLowerCase()} now to ${cur.due} people? There is one a month, so nobody gets a second one. This cannot be undone.`)) return;
    setBusy(`more-send-${row.kind}`);
    try {
      const r = await api.post("/admin/mail/more/send", { kind: row.kind });
      if (r.data.queued) toast.success(`Emailing ${r.data.queued} people now.`);
      else toast.error(MORE_NOT_SENT[r.data.reason] || "The email was not sent.");
      await loadMore();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the email"); }
    finally { setBusy(""); }
  };

  const saveDay = async (e) => {
    setBusy("more-day");
    try {
      await api.put("/admin/mail/more/day", { day: Number(e.target.value) });
      toast.success("Saved");
      await loadMore();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save that"); }
    finally { setBusy(""); }
  };

  // DJ Booth introduction: one switch, and a test to yourself for any of the six weeks.
  const flipDj = async () => {
    if (!dj.auto && dj.not_tried > 0
        && !window.confirm(`Turn on the DJ Booth emails?\n\nThe Premium members who have not tried the booth yet (${dj.not_tried}) get week 1 over the next few minutes. After that, each gets one a week, six at most, and they stop the moment a member connects a screen to the booth.`)) return;
    setBusy("dj-auto");
    try {
      const r = await api.put("/admin/mail/dj/auto", { on: !dj.auto });
      toast.success(r.data.auto ? "The DJ Booth emails are on" : "The DJ Booth emails are off");
      await loadDj();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change that"); }
    finally { setBusy(""); }
  };

  const testDj = async () => {
    setBusy("dj-test");
    try {
      const r = await api.post("/admin/mail/dj/test", { week: Number(djWeek) });
      toast.success(`Test of week ${r.data.week} sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the test"); }
    finally { setBusy(""); }
  };

  const pickedTitle = data && data.all_songs ? ((data.all_songs.find((x) => x.id === pick) || {}).title || "") : "";

  // Featured song email: any song on the site, as often as Dan likes.
  const sendFeatured = async () => {
    if (!pick) { toast.error("Pick a song first"); return; }
    const line = note.trim();
    if (!window.confirm(`Email ${data.count} people about "${pickedTitle}"?\n\nSubject: Featured on Dan The Man TV: ${pickedTitle}${line ? `\nYour line: ${line}` : ""}\n\nPeople who unsubscribed are left out. This cannot be undone.`)) return;
    setBusy("featured");
    try {
      const r = await api.post("/admin/mail/featured", { video_id: pick, note: line });
      if (r.data.queued) { toast.success(`Emailing ${r.data.queued} people now.`); setNote(""); }
      else toast.error(FEATURED_NOT_SENT[r.data.reason] || "The email was not sent.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the email"); }
    finally { setBusy(""); }
  };

  const testFeatured = async () => {
    if (!pick) { toast.error("Pick a song first"); return; }
    setBusy("featured-test");
    try {
      const r = await api.post("/admin/mail/featured-test", { video_id: pick, note: note.trim() });
      toast.success(`Test sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the test"); }
    finally { setBusy(""); }
  };

  // A send that stopped short: email only the members who did not get it. Nobody gets a second one.
  const catchUp = async (s) => {
    if (!window.confirm(`Email the ${s.mail.missed} people who did not get "${s.title}"? Nobody who already got it will get a second one.`)) return;
    setBusy(s.id);
    try {
      const r = await api.post(`/admin/mail/catchup/${s.id}`);
      if (r.data.queued) toast.success(`Emailing ${r.data.queued} people now.`);
      else toast.error(NOT_SENT[r.data.reason] || "The email was not sent.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the email"); }
    finally { setBusy(""); }
  };

  const savedAddress = data ? data.address : "";
  const changed = address.trim() !== (savedAddress || "").trim();

  return (
    <div data-testid="admin-mailers">
      <h2 className="font-display text-2xl font-bold mb-4">Email to members</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6 space-y-6">
        <p className="text-xs text-zinc-500">
          When you add a song with the New song box ticked, your members get one email about it. Only members with a login and a confirmed email who have not unsubscribed get it,
          plus people who signed up in the box at the bottom of the site and confirmed. Every email has an unsubscribe link.{data ? ` Right now that is ${data.count} people.` : ""}
        </p>
        {failed && <div className="text-sm text-zinc-400">This section could not be loaded. Refresh the page and try again.</div>}

        <div>
          <label htmlFor="admin-mail-address" className="text-sm text-zinc-200">Your mailing address</label>
          <textarea id="admin-mail-address" data-testid="admin-mail-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={3} maxLength={300}
            placeholder={"PO Box 123\nAustin, TX 78701"}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
          <p className="mt-1 text-xs text-zinc-500">Shown at the bottom of every email. Email rules in the US ask for a real mailing address, and a P.O. box works. No emails go out until this is saved.</p>
          <button type="button" onClick={saveAddress} disabled={busy === "address" || !changed} data-testid="admin-mail-address-save"
            className="mt-2 px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-40">Save address</button>
        </div>

        <div data-testid="admin-leave-out">
          <label htmlFor="admin-leave-text" className="text-sm text-zinc-200">Leave these addresses out</label>
          <textarea id="admin-leave-text" data-testid="admin-leave-text" value={leaveText} onChange={(e) => setLeaveText(e.target.value)} rows={4}
            placeholder={"tester1@gmail.com\ntester2@gmail.com"} spellCheck={false} autoCapitalize="off"
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono-ui focus:outline-none focus:border-cyan-400/50" />
          <p className="mt-1 text-xs text-zinc-500">
            One address per line. These never get any of the emails to the members: new songs, featured songs, the welcome, come-back, resubscribe and monthly emails.
            Use it for your test accounts, or for anyone who asks you to stop and does not use the Unsubscribe link. It stays in place even if that person deletes their account and signs up again.
            Emails about their account, like password resets and billing notices, still go out, and a test email to yourself still works.
          </p>
          <button type="button" onClick={saveLeave} disabled={busy === "leave" || (leave && leaveText.trim() === (leave.text || "").trim())} data-testid="admin-leave-save"
            className="mt-2 px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-40">Save the list</button>
          {leave && (
            <div className="mt-2 text-xs text-zinc-400" data-testid="admin-leave-status">
              {leave.count === 0 ? "Nobody is left out." : `${leave.count} address${leave.count === 1 ? "" : "es"} saved. ${leave.on_site} of them ${leave.on_site === 1 ? "is an account" : "are accounts"} on the site.`}
            </div>
          )}
        </div>

        <div data-testid="admin-signups">
          <div className="text-sm text-zinc-200">Sign-ups without a login</div>
          <p className="mt-1 text-xs text-zinc-500">
            People who typed their address in the box at the bottom of the site. They are on the list only after they press the button in the email we send them,
            and then they get new songs, featured songs and the monthly roundup. You cannot add anyone by hand. To stop emailing someone who asks, add their address to the leave-out list above.
          </p>
          {signups && (
            <>
              <div className="mt-2 text-xs text-zinc-400" data-testid="admin-signups-counts">
                {signups.getting_emails} getting emails · {signups.pending} waiting to confirm · {signups.unsubscribed} unsubscribed
              </div>
              {signups.latest.length > 0 && (
                <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-white/10 rounded-lg border border-white/10" data-testid="admin-signups-list">
                  {signups.latest.map((x, i) => (
                    <div key={`${x.email}-${i}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 px-3 py-1.5 text-xs">
                      <span className="break-all text-zinc-200">{x.email}</span>
                      <span className={x.status === "confirmed" ? "text-cyan-300" : "text-zinc-500"}>
                        {x.status === "confirmed" ? "On the list" : x.status === "pending" ? "Waiting to confirm" : "Unsubscribed"}{x.at ? ` · ${dayText(x.at)}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <div className="text-sm text-zinc-200">See it first</div>
          <p className="mt-1 text-xs text-zinc-500">Sends the email for your newest song to your own address only, so you can see how it looks before the members get one.</p>
          <button type="button" onClick={sendTest} disabled={busy === "test"} data-testid="admin-mail-test"
            className="mt-2 px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5 disabled:opacity-40">{busy === "test" ? "Sending..." : "Email me a test"}</button>
        </div>

        <div data-testid="admin-welcome">
          <div className="text-sm text-zinc-200">Welcome email</div>
          <p className="mt-1 text-xs text-zinc-500">
            A short tour of the site for new members: playing songs, playlists, the lights, Beat mode and color codes. Each member gets it once.
          </p>
          {welcome && (
            <>
              <div className="mt-3 flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <div id="admin-welcome-auto-label" className="text-sm">Send it to new members automatically</div>
                  <p className="mt-1 text-xs text-zinc-500">It goes out a couple of minutes after someone joins. Only people who join after you turn this on get it. Starts off.</p>
                </div>
                <button type="button" role="switch" aria-checked={welcome.auto} aria-labelledby="admin-welcome-auto-label" onClick={flipWelcomeAuto}
                  disabled={busy === "welcome-auto" || (!welcome.auto && !welcome.address_set)} data-testid="admin-welcome-auto"
                  title={!welcome.auto && !welcome.address_set ? "Save your mailing address first" : undefined}
                  className={`shrink-0 relative w-12 h-7 rounded-full border transition-colors disabled:opacity-60 ${welcome.auto ? "bg-cyan-400 border-cyan-300" : "bg-white/10 border-white/20"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${welcome.auto ? "translate-x-5" : ""}`} />
                </button>
              </div>
              <div className="mt-1 text-xs text-zinc-400" data-testid="admin-welcome-auto-state">{welcome.auto ? "On" : "Off"}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={testWelcome} disabled={busy === "welcome-test"} data-testid="admin-welcome-test"
                  className="px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5 disabled:opacity-40">{busy === "welcome-test" ? "Sending..." : "Email me a test of the welcome"}</button>
                <button type="button" onClick={sendWelcomeExisting} disabled={!welcome.address_set || welcome.waiting === 0 || welcomeSending || busy === "welcome-existing"} data-testid="admin-welcome-existing"
                  title={welcome.address_set ? "Send the welcome once to members who have not had it" : "Save your mailing address first"}
                  className="px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-40">
                  {welcome.waiting === 0 ? "Everyone has had the welcome" : `Send it once to the ${welcome.waiting} who have not had it`}
                </button>
              </div>
              <div className="mt-2 text-xs text-zinc-400" data-testid="admin-welcome-status">
                Welcomed so far: {welcome.sent_total}
                {welcome.run ? (welcome.run.status === "sending"
                  ? ` · Sending... ${welcome.run.sent + welcome.run.failed} of ${welcome.run.total}`
                  : ` · Last once-only send ${dayText(welcome.run.created_at)}: ${welcome.run.sent} sent${welcome.run.failed ? `, ${welcome.run.failed} could not be sent` : ""}`) : ""}
              </div>
            </>
          )}
        </div>

        <div data-testid="admin-featured-song">
          <div className="text-sm text-zinc-200">Featured song email</div>
          <p className="mt-1 text-xs text-zinc-500">
            Pick any song on the site and email it to your members as a featured song, as many times as you like. Good for a song that comes back every year.
            It only sends an email. It does not change the Featured star or the Home banner.
          </p>
          <label htmlFor="admin-featured-pick" className="sr-only">Song to feature</label>
          <select id="admin-featured-pick" data-testid="admin-featured-pick" value={pick} onChange={(e) => setPick(e.target.value)} style={{ colorScheme: "dark" }}
            className="mt-2 w-full bg-zinc-900 text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50">
            <option value="" className="bg-zinc-900 text-white">Pick a song...</option>
            {(data && data.all_songs ? data.all_songs : []).map((x) => <option key={x.id} value={x.id} className="bg-zinc-900 text-white">{x.title}</option>)}
          </select>
          <label htmlFor="admin-featured-note" className="sr-only">One line to add to the email</label>
          <input id="admin-featured-note" data-testid="admin-featured-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={160}
            placeholder="One line to add to the email (optional), like: Oktoberfest is here!"
            className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={sendFeatured} disabled={!savedAddress || !pick || busy === "featured"} data-testid="admin-featured-send"
              title={savedAddress ? "Email the members about this song" : "Save your mailing address first"}
              className="px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-40">Send featured song</button>
            <button type="button" onClick={testFeatured} disabled={!pick || busy === "featured-test"} data-testid="admin-featured-test"
              className="px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5 disabled:opacity-40">{busy === "featured-test" ? "Sending..." : "Email me a test of this"}</button>
          </div>
          {data && data.featured && data.featured.length > 0 && (
            <div className="mt-3 divide-y divide-white/10" data-testid="admin-featured-sent">
              {data.featured.map((f) => (
                <div key={f.id} className="py-2 text-xs">
                  <span className="text-zinc-200">{f.title}</span>{" "}
                  <span className="text-zinc-400">
                    {f.status === "sending" ? `Sending... ${f.sent + f.failed} of ${f.total}` : `${dayText(f.created_at)}: ${f.sent} sent${f.failed ? `, ${f.failed} could not be sent` : ""}`}
                  </span>
                  {f.note ? <span className="text-zinc-500"> &ldquo;{f.note}&rdquo;</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="admin-more">
          <div className="text-sm text-zinc-200">Come-back and monthly emails</div>
          <p className="mt-1 text-xs text-zinc-500">
            These go out by themselves once you switch them on. Every member is in only one group, so nobody gets more than one of these in a month.
            A member counts as away after 30 days without a visit, and after a year away these emails stop. Only members on the email list get them, and each has an unsubscribe link. All three start off.
          </p>
          {more && (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                <label htmlFor="admin-more-day">The monthly ones go out on day</label>
                <select id="admin-more-day" data-testid="admin-more-day" value={more.day} onChange={saveDay} disabled={busy === "more-day"} style={{ colorScheme: "dark" }}
                  className="bg-zinc-900 text-white border border-white/10 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-cyan-400/50">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d} className="bg-zinc-900 text-white">{d}</option>)}
                </select>
                <span>of the month, around 10 am in Austin.</span>
              </div>
              <div className="mt-3 divide-y divide-white/10">
                {MORE_ROWS.map((row) => {
                  const cur = more[row.kind];
                  const monthly = row.kind !== "comeback";
                  const sendingNow = !!cur.last && cur.last.status === "sending";
                  const labelId = `admin-more-${row.kind}-label`;
                  return (
                    <div key={row.kind} className="py-4 first:pt-0" data-testid={`admin-more-${row.kind}`}>
                      <div className="flex items-start gap-4">
                        <div className="min-w-0 flex-1">
                          <div id={labelId} className="text-sm">{row.title}</div>
                          <p className="mt-1 text-xs text-zinc-500">{row.text}</p>
                        </div>
                        <Switch on={cur.auto} labelId={labelId} onClick={() => flipMore(row)} testid={`admin-more-${row.kind}-switch`}
                          disabled={busy === `more-${row.kind}` || (!cur.auto && !more.address_set)}
                          title={!cur.auto && !more.address_set ? "Save your mailing address first" : undefined} />
                      </div>
                      <div className="mt-1 text-xs text-zinc-400" data-testid={`admin-more-${row.kind}-state`}>{cur.auto ? "On" : "Off"}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={() => testMore(row)} disabled={busy === `more-test-${row.kind}`} data-testid={`admin-more-${row.kind}-test`}
                          className="px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5 disabled:opacity-40">{busy === `more-test-${row.kind}` ? "Sending..." : row.test}</button>
                        {monthly && (
                          <button type="button" onClick={() => sendMore(row)} disabled={!more.address_set || sendingNow || cur.due === 0 || busy === `more-send-${row.kind}`} data-testid={`admin-more-${row.kind}-send`}
                            title={more.address_set ? "Send this month's email now" : "Save your mailing address first"}
                            className="px-4 py-1.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-40">Send this month&rsquo;s now</button>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-zinc-400" data-testid={`admin-more-${row.kind}-status`}>
                        {row.kind === "comeback" && `${cur.due} would get it right now · Emailed so far: ${cur.sent_total}`}
                        {row.kind === "resubscribe" && `${cur.due} would get it this month`}
                        {row.kind === "roundup" && `${cur.new_songs} new songs since the last roundup · goes to ${cur.due} people`}
                        {monthly && cur.next ? ` · Next automatic send: ${dayText(cur.next)}` : ""}
                        {monthly && cur.last ? ` · Last: ${lastText(cur.last)}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div data-testid="admin-dj">
          <div className="text-sm text-zinc-200">DJ Booth emails</div>
          <p className="mt-1 text-xs text-zinc-500">
            A short introduction to the DJ Booth for Premium members, until they try it: play your own videos and music on a smart TV, with the phone as the controller.
            Each member gets one a week, six at most, and they stop the moment the member connects a screen to the booth. Only Premium members on the email list get them
            (not you), and each email has an unsubscribe link.
          </p>
          {dj && (
            <>
              <div className="mt-3 flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <div id="admin-dj-auto-label" className="text-sm">Send the DJ Booth emails</div>
                  <p className="mt-1 text-xs text-zinc-500">When you turn this on, Premium members who have not tried the booth get week 1 right away. A member who becomes Premium later gets it two days after. Starts off.</p>
                </div>
                <Switch on={dj.auto} labelId="admin-dj-auto-label" onClick={flipDj} testid="admin-dj-auto"
                  disabled={busy === "dj-auto" || (!dj.auto && !dj.address_set)}
                  title={!dj.auto && !dj.address_set ? "Save your mailing address first" : undefined} />
              </div>
              <div className="mt-1 text-xs text-zinc-400" data-testid="admin-dj-auto-state">{dj.auto ? "On" : "Off"}</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label htmlFor="admin-dj-week" className="text-xs text-zinc-400">Test week</label>
                <select id="admin-dj-week" data-testid="admin-dj-week" value={djWeek} onChange={(e) => setDjWeek(e.target.value)} style={{ colorScheme: "dark" }}
                  className="bg-zinc-900 text-white border border-white/10 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-cyan-400/50">
                  {[1, 2, 3, 4, 5, 6].map((w) => <option key={w} value={w} className="bg-zinc-900 text-white">{w}</option>)}
                </select>
                <button type="button" onClick={testDj} disabled={busy === "dj-test"} data-testid="admin-dj-test"
                  className="px-4 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/5 disabled:opacity-40">{busy === "dj-test" ? "Sending..." : "Email me a test of the DJ Booth email"}</button>
              </div>
              <div className="mt-2 text-xs text-zinc-400" data-testid="admin-dj-status">
                {dj.premium} Premium members on the email list · {dj.tried} have tried the booth · {dj.not_tried} have not
                {dj.finished ? ` (${dj.finished} of them have had all six emails)` : ""} · Emails sent so far: {dj.sent_total}
                {dj.stopped ? ` · ${dj.stopped} stopped just the DJ Booth emails` : ""}
              </div>
            </>
          )}
        </div>

        <div>
          <div className="text-sm text-zinc-200">Your latest songs</div>
          <p className="mt-1 text-xs text-zinc-500">A song you did not tick New song for can be emailed here, once.</p>
          {data && data.songs.length === 0 && <div className="mt-3 text-sm text-zinc-500">No songs yet.</div>}
          <div className="mt-2 divide-y divide-white/10" data-testid="admin-mail-songs">
            {(data ? data.songs : []).map((s) => (
              <div key={s.id} className="py-3 flex items-center gap-3" data-testid={`admin-mail-song-${s.id}`}>
                {s.cover_url ? <img src={s.cover_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" /> : <div className="w-10 h-10 rounded bg-white/5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-xs"><MailStatus mail={s.mail} /></div>
                </div>
                {!s.mail && (
                  <button type="button" onClick={() => sendFor(s)} disabled={!savedAddress || busy === s.id} data-testid={`admin-mail-send-${s.id}`}
                    title={savedAddress ? "Email the members about this song" : "Save your mailing address first"}
                    className="shrink-0 px-3 py-1.5 rounded-full border border-white/20 text-xs hover:bg-white/5 disabled:opacity-40">Email members</button>
                )}
                {s.mail && s.mail.status === "done" && s.mail.missed > 0 && (
                  <button type="button" onClick={() => catchUp(s)} disabled={!savedAddress || busy === s.id} data-testid={`admin-mail-catchup-${s.id}`}
                    title={savedAddress ? "Email the members who did not get it" : "Save your mailing address first"}
                    className="shrink-0 px-3 py-1.5 rounded-full bg-cyan-400 text-black text-xs font-semibold hover:bg-cyan-300 disabled:opacity-40">Send to the {s.mail.missed} who missed it</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
